import {AddressChecking} from "./address-status/AddressChecking.js";
import {AddressCloseMatch} from "./address-status/AddressCloseMatch.js";
import {AddressNotFound} from "./address-status/AddressNotFound.js";
import {AddressRecognized} from "./address-status/AddressRecognized.js";
import {ManualPinFrozen} from "./address-status/ManualPinFrozen.js";
import {ManualPinPlacement} from "./address-status/ManualPinPlacement.js";
import type {
  AddressVerification,
  MapCenterPrecision,
  StructuredReplacementFields,
} from "./useAddressVerification.js";

type Props = {
  /**
   * Machine à états de vérification (`useAddressVerification`), tenue par le
   * formulaire appelant : ce composant n'est qu'un DISPATCH de `verification.status`
   * vers le composant de rendu correspondant (`address-status/`) — il ne
   * possède aucun état propre (#937).
   */
  verification: AddressVerification;
  /**
   * Écrit rue/CP/ville dans le formulaire RHF de l'appelant au clic
   * « Remplacer » (#980) — le hook ne possède pas d'instance RHF, cette
   * écriture reste chez l'appelant (même pattern que `payloadPin`/`dropPin`).
   * Jamais appelé si `verification.structuredReplacement` est `null` (le
   * bouton n'est rendu que quand il ne l'est pas).
   */
  onReplace?: (fields: StructuredReplacementFields) => void;
  // Garde-fou de précision du centrage carte, threading vers ManualPinPlacement — voir AddressVerificationSection (source du threading, #1022).
  mapCenterPrecision?: MapCenterPrecision | null;
};

export function AddressGeocodeStatus({verification, onReplace, mapCenterPrecision}: Props) {
  const {status, mapMode, mapCenter, pinPosition, fields} = verification;

  /**
   * Ordre de dispatch — de la logique, pas du style :
   * 1. Mode carte avant tout : navigation UI, remplace tout le rendu normal,
   *    indépendante du verdict de résolution.
   * 2. `status` ensuite, dérivé une seule fois par le hook — ce composant ne
   *    recalcule aucun verdict d'affichage, il ne fait que dispatcher.
   */
  if (mapMode && mapCenter) {
    return (
      <ManualPinPlacement
        fields={fields}
        /**
         * `pinPosition` (le dernier drop de CETTE session) prime sur
         * `mapCenter` (figé à l'ouverture) — sinon le marqueur revient
         * visuellement au centre initial à chaque re-rendu post-drop (#1011).
         */
        position={pinPosition ?? mapCenter}
        onDragEnd={verification.dropPin}
        onBack={verification.closeMap}
        mapCenterPrecision={mapCenterPrecision}
      />
    );
  }
  switch (status) {
    case "manual-pin":
      return <ManualPinFrozen onRemovePin={verification.removePin} />;
    case "checking":
      return <AddressChecking />;
    case "recognized":
      return verification.geocode ? (
        <AddressRecognized label={verification.geocode.label} />
      ) : null;
    case "close-match":
      return verification.structuredReplacement ? (
        <AddressCloseMatch
          fields={fields}
          replacement={verification.structuredReplacement}
          onReplace={onReplace ? handleReplace : undefined}
          onPositionOnMap={verification.openMap}
        />
      ) : null;
    case "not-found": {
      /**
       * Rien à afficher tant qu'aucune tentative de géocodage n'a eu lieu
       * (saisie vide/incomplète, ni géocode ni erreur posés) — sinon la carte
       * "introuvable" flasherait avant même que le visiteur ait fini de
       * taper (#799). Un géocode PRÉSENT mais de précision non exploitable
       * (municipality/unknown, ou toponym/locality sans composants
       * structurés — #1082) compte comme une tentative : `error` reste vide
       * dans ce cas (l'appel IGN a réussi).
       */
      const hasAttempted = verification.geocode != null || verification.error !== "";
      return hasAttempted ? (
        <AddressNotFound
          error={verification.error}
          resolvingMapCenter={verification.resolvingMapCenter}
          onPositionOnMap={verification.openMap}
        />
      ) : null;
    }
    default:
      return null;
  }

  // Écrit les champs ET notifie le hook (télémétrie "confirmed") au même clic.
  function handleReplace() {
    const replacement = verification.structuredReplacement;
    if (!replacement) {
      return;
    }
    verification.replace();
    onReplace?.(replacement);
  }
}
